import useAdminDatasetUsers from "admin/hooks/useAdminDatasetUsers";
import {
  DatasetGrantAccessLevel,
  DatasetGroupAccess,
  DatasetUserAccess,
} from "admin/types/interfaces/adminDatasetUsers";
import { Loader2 } from "lucide-react";
import { Badge, BadgeProps } from "~/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";

interface AccessBadgeConfig {
  label: string;
  variant: BadgeProps["variant"];
}

// Covers every DatasetGrantAccessLevel the endpoint can return; mirrors the
// styling of DatasetAccessBadge and adds "discover", which that badge omits.
const ACCESS_BADGE_CONFIG: Record<DatasetGrantAccessLevel, AccessBadgeConfig> = {
  owner: { label: "Owner", variant: "default" },
  analyze: { label: "Analyst", variant: "secondary" },
  preview: { label: "Preview", variant: "outline" },
  discover: { label: "Discover", variant: "outline" },
  "no access": { label: "No access", variant: "muted" },
};

const AccessBadge = ({ accessLevel }: { accessLevel: DatasetGrantAccessLevel }) => {
  const config = ACCESS_BADGE_CONFIG[accessLevel] ?? ACCESS_BADGE_CONFIG["no access"];
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const groupDisplayName = (group: DatasetGroupAccess) => group.display_name ?? group.name;

// Ordering of access levels for the sorted listings: owner first, then analyze,
// preview, discover. "no access" sorts last defensively — the endpoint only
// returns users/groups that have access, so it shouldn't appear here.
const ACCESS_LEVEL_RANK: Record<DatasetGrantAccessLevel, number> = {
  owner: 0,
  analyze: 1,
  preview: 2,
  discover: 3,
  "no access": 4,
};

const byUserAccessThenEmail = (a: DatasetUserAccess, b: DatasetUserAccess) =>
  ACCESS_LEVEL_RANK[a.access_level] - ACCESS_LEVEL_RANK[b.access_level] ||
  a.email.localeCompare(b.email);

const byGroupAccessThenName = (a: DatasetGroupAccess, b: DatasetGroupAccess) =>
  ACCESS_LEVEL_RANK[a.access_level] - ACCESS_LEVEL_RANK[b.access_level] ||
  groupDisplayName(a).localeCompare(groupDisplayName(b));

interface DatasetUsersDialogProps {
  /** The dataset whose users to show; the dialog is open while this is non-null. */
  datasetId: string | null;
  datasetTitle: string | undefined;
  onClose: () => void;
}

/**
 * "View Users" modal, split out from the datasets table (like AddTableDialog) so
 * its data fetch and re-renders stay isolated from the up-to-500-row table.
 */
const DatasetUsersDialog = ({ datasetId, datasetTitle, onClose }: DatasetUsersDialogProps) => {
  const { users, groups, isLoading, error } = useAdminDatasetUsers(datasetId);

  const sortedUsers = [...users].sort(byUserAccessThenEmail);
  const sortedGroups = [...groups].sort(byGroupAccessThenName);

  return (
    <Dialog open={datasetId !== null} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Dataset Users</DialogTitle>
          <DialogDescription>
            <span className="font-semibold">{datasetTitle}</span>
            <span className="font-mono text-xs ml-1.5">({datasetId})</span>
          </DialogDescription>
          <DialogDescription>
            Users and groups from Access Controller with access to this dataset, and the access
            level each holds.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading users...</span>
          </div>
        ) : error ? (
          <p className="py-8 text-center text-destructive">Error loading users</p>
        ) : (
          <div className="max-h-[60vh] space-y-6 overflow-auto">
            <section>
              <h3 className="mb-2 text-sm font-semibold">Users</h3>
              {users.length === 0 ? (
                <p className="text-sm text-muted-foreground">No users have direct access.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-[120px]">Access</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedUsers.map((user) => (
                      <TableRow key={user.email}>
                        <TableCell className="font-mono text-sm">{user.email}</TableCell>
                        <TableCell>{user.full_name ?? "—"}</TableCell>
                        <TableCell>
                          <AccessBadge accessLevel={user.access_level} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">Groups</h3>
              {groups.length === 0 ? (
                <p className="text-sm text-muted-foreground">No groups have access.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Group</TableHead>
                      <TableHead className="w-[100px]">Members</TableHead>
                      <TableHead className="w-[120px]">Access</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedGroups.map((group) => (
                      <TableRow key={group.name}>
                        <TableCell className="font-semibold">{groupDisplayName(group)}</TableCell>
                        <TableCell>{group.users.length}</TableCell>
                        <TableCell>
                          <AccessBadge accessLevel={group.access_level} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DatasetUsersDialog;
